import { Autocomplete, TextField } from '@mui/material';
import TextInput from 'components/Common/TextInput';
import Button from 'components/Common/Button';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, store } from 'redux/store';
import { toUpdateMePayload, userUpdateInfo } from 'redux/slices/user/userSlice';
import { selectEffectiveStaffRole, selectHasPortalStaffAccess } from 'redux/selectors/parishContext';
import countries from 'Data/countries.json';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import routes from 'constants/routes';
import CardWrapper from './CardWrapper';

interface EditContainerProps {
    children?: React.ReactNode;
    type: 'account' | 'staff';
    defaultValues?: any;
}

const formSchema = yup.object({
    firstName: yup.string().required('First name is required.'),
    lastName: yup.string().required('Last name is required.'),
    address: yup.object({
        address1: yup.string().required('Address1 is required.'),
        city: yup.string().required('City is required.'),
        state: yup.string().required('State is required.'),
        country: yup.string().required('Country is required.'),
        zip: yup.string().required('ZipCode is required.'),
    }),
});

function EditContainer({ children, type, defaultValues }: EditContainerProps) {
    const navigate = useNavigate();
    const { updateInfoLoading, hasStaffAccess, effectiveRole, selectedParishName } = useSelector((state: RootState) => ({
        updateInfoLoading: state.user?.updateInfoLoading,
        hasStaffAccess: selectHasPortalStaffAccess(state),
        effectiveRole: selectEffectiveStaffRole(state),
        selectedParishName: state.user.selectedParishName,
    }));
    const dispatch = useDispatch<typeof store.dispatch>();
    const isAccountType = type === 'account';
    const address = { isDefault: true, ...defaultValues?.address };
    const {
        handleSubmit,
        setValue,
        watch,
        control,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(formSchema),
        defaultValues: { ...defaultValues, address },
    });

    const onSubmit = async (formValues: any) => {
        const payload = toUpdateMePayload(formValues);
        const res: any = await dispatch(userUpdateInfo(payload));
        if (!res?.error) {
            navigate(routes.account.main);
        }
    };

    const getCountry = (code: string) => {
        return countries.find((c) => c.code === code);
    };

    const nameLabelPrefix = hasStaffAccess ? 'Account Owner ' : '';

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <CardWrapper sx={{ my: 6 }}>
                {children}
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Controller
                        name="firstName"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                sx={{ mt: 2, mb: 2 }}
                                width="49%"
                                label={`${nameLabelPrefix}First Name`}
                                placeholder="Enter your first name"
                                icon={null}
                                error={!!errors.firstName}
                                helperText={(errors?.firstName as any)?.message}
                            />
                        )}
                    />
                    <Controller
                        name="lastName"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                sx={{ mt: 2, mb: 2 }}
                                width="49%"
                                placeholder="Enter your last name"
                                iconPosition="end"
                                label={`${nameLabelPrefix}Last Name`}
                                icon={null}
                                error={!!errors.lastName}
                                helperText={(errors?.lastName as any)?.message}
                            />
                        )}
                    />
                </div>
                <TextInput name={'email'} value={watch('email')} sx={{ mt: 2, mb: 2 }} width="100%" label="Email" icon={null} disabled />
                {selectedParishName ? (
                    <TextInput
                        sx={{ mt: 2, mb: 2 }}
                        width="100%"
                        label="Parish"
                        icon={null}
                        value={selectedParishName}
                        name="parishName"
                        disabled
                    />
                ) : null}
                {effectiveRole ? (
                    <TextInput
                        sx={{ mt: 2, mb: 2 }}
                        width="100%"
                        label="Role"
                        icon={null}
                        value={effectiveRole.replace(/_/g, ' ')}
                        name="role"
                        disabled
                    />
                ) : null}
                <TextInput
                    name="telephone"
                    value={watch('telephone') || ''}
                    sx={{ mt: 2, mb: 2 }}
                    width="100%"
                    label="Contact Number"
                    placeholder="Contact Number"
                    icon={null}
                    disabled
                    helperText="Phone changes are not available on this screen yet."
                />
                <Controller
                    name="address.address1"
                    control={control}
                    render={({ field }) => (
                        <TextInput
                            {...field}
                            sx={{ mt: 2, mb: 2 }}
                            width="100%"
                            label="Address1"
                            placeholder="Address1"
                            icon={null}
                            error={!!(errors?.address as any)?.address1}
                            helperText={(errors?.address as any)?.address1?.message}
                        />
                    )}
                />
                <TextInput
                    sx={{ mt: 2, mb: 2 }}
                    width="100%"
                    label="Address2"
                    placeholder="Address2"
                    icon={null}
                    name={'address.address2'}
                    value={watch('address.address2')}
                    setValue={setValue}
                />
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Controller
                        name="address.city"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                sx={{ mt: 2, mb: 2 }}
                                width="49%"
                                label="City"
                                placeholder="City"
                                icon={null}
                                error={!!(errors.address as any)?.city}
                                helperText={(errors.address as any)?.city?.message}
                            />
                        )}
                    />

                    <Controller
                        name="address.state"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                sx={{ mt: 2, mb: 2 }}
                                width="49%"
                                label="State"
                                placeholder="State"
                                icon={null}
                                error={!!(errors.address as any)?.state}
                                helperText={(errors.address as any)?.state?.message}
                            />
                        )}
                    />
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Controller
                        name="address.zip"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                sx={{ mt: 2, mb: 2 }}
                                width="49%"
                                label="Zip code"
                                placeholder="Zip code"
                                icon={null}
                                error={!!(errors.address as any)?.zip}
                                helperText={(errors.address as any)?.zip?.message}
                            />
                        )}
                    />
                    <Controller
                        name="address.country"
                        control={control}
                        render={({ field }) => (
                            <Autocomplete
                                {...field}
                                id="address-country-select"
                                options={countries}
                                sx={{ width: '49%', mt: 2, mb: 2 }}
                                getOptionLabel={(option) => option.name}
                                value={getCountry(watch('address.country'))}
                                onChange={(event, value) => {
                                    setValue('address.country', value?.code || '');
                                    field.onChange(value?.code);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Country"
                                        error={!!(errors?.address as any)?.country}
                                        helperText={(errors?.address as any)?.country ? (errors?.address as any)?.country?.message : ''}
                                    />
                                )}
                            />
                        )}
                    />
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: !isAccountType ? 12 : 26 }}>
                    <Button loading={updateInfoLoading} type="submit" sx={{ width: { xs: '80%', sm: '50%' } }} title="Save" />
                </div>
            </CardWrapper>
        </form>
    );
}

export default EditContainer;
